import apiClient from "./apiClient";
import AppError from "../utils/AppError";
import Asset from "../models/Asset";
import Inspection from "../models/Inspection";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../utils/notifications";

const AssetService = {
  async getAsset(assetUuid) {
    try {
      const { data } = await apiClient.get(`/assets/${assetUuid}`);
      return Asset.fromServerModel(data);
    } catch (error) {
      let userMessage = "Unable to load asset data. Please try again.";

      if (error.response?.data?.detail) {
        userMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        userMessage = error.response.data.error;
      } else if (error.response?.status === 404) {
        userMessage = "Asset not found. It may have been deleted or moved.";
      } else if (error.response?.status === 403) {
        userMessage = "You don't have permission to view this asset.";
      } else if (error.response?.status >= 500) {
        userMessage = "Server error occurred. Please try again later.";
      }

      const appError = new AppError({
        title: "Failed to Fetch Asset",
        response: error.response,
        userMessage,
      });
      // The page renders an access-denied view for 403, so a toast would
      // only duplicate it.
      if (error.response?.status !== 403) {
        showErrorNotification(appError);
      }
      throw appError;
    }
  },

  async patchAsset(assetUuid, payload, withAssetId = false, silent = false) {
    const assetPayload = new Asset(payload).toServerModel();
    const editableFields = [
      "LocationStreet",
      "LocationTown",
      "Material",
      "HeightDiameter",
      "UpstreamNode",
      "DownstreamNode",
    ];

    if (withAssetId) {
      editableFields.push("AssetID");
    }

    const reducedPayload = Object.keys(assetPayload).reduce((acc, key) => {
      if (editableFields.includes(key)) {
        acc[key] = assetPayload[key];
      }
      return acc;
    }, {});
    try {
      const { data } = await apiClient.patch(
        `/assets/${assetUuid}`,
        reducedPayload
      );
      if (!silent) {
        showSuccessNotification("Success", "Asset updated successfully!");
      }
      return Asset.fromServerModel(data);
    } catch (error) {
      let userMessage =
        "Unable to save changes. Please check your input and try again.";

      if (error.response?.data?.detail) {
        userMessage = error.response.data.detail;
      } else if (error.response?.data?.non_field_errors) {
        userMessage = Array.isArray(error.response.data.non_field_errors)
          ? error.response.data.non_field_errors.join(", ")
          : error.response.data.non_field_errors;
      } else if (error.response?.data?.AssetID) {
        userMessage = `Asset ID error: ${
          Array.isArray(error.response.data.AssetID)
            ? error.response.data.AssetID.join(", ")
            : error.response.data.AssetID
        }`;
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        userMessage = error.response.data.error;
      } else if (error.response?.status === 400) {
        userMessage = "Invalid data provided. Please check all fields.";
      } else if (error.response?.status === 403) {
        userMessage = "You don't have permission to update this asset.";
      } else if (error.response?.status === 404) {
        userMessage = "Asset not found. It may have been deleted.";
      } else if (error.response?.status === 409) {
        userMessage = "Asset ID already exists. Please use a different ID.";
      } else if (error.response?.status >= 500) {
        userMessage = "Server error occurred. Please try again later.";
      }

      const appError = new AppError({
        title: "Failed to Update Asset",
        response: error.response,
        userMessage,
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async createAsset(payload) {
    try {
      const { data } = await apiClient.post(
        "/assets",
        new Asset(payload).toServerModel()
      );
      showSuccessNotification("Success", "Asset created successfully!");
      return Asset.fromServerModel(data);
    } catch (error) {
      let userMessage =
        "Unable to create asset. Please check your input and try again.";

      if (error.response?.data?.detail) {
        userMessage = error.response.data.detail;
      } else if (error.response?.data?.non_field_errors) {
        userMessage = Array.isArray(error.response.data.non_field_errors)
          ? error.response.data.non_field_errors.join(", ")
          : error.response.data.non_field_errors;
      } else if (error.response?.data?.AssetID) {
        userMessage = `Asset ID error: ${
          Array.isArray(error.response.data.AssetID)
            ? error.response.data.AssetID.join(", ")
            : error.response.data.AssetID
        }`;
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        userMessage = error.response.data.error;
      } else if (error.response?.status === 400) {
        userMessage =
          "Invalid data provided. Please check all required fields.";
      } else if (error.response?.status === 403) {
        userMessage = "You don't have permission to create assets.";
      } else if (error.response?.status === 409) {
        userMessage = "Asset ID already exists. Please use a different ID.";
      } else if (error.response?.status >= 500) {
        userMessage = "Server error occurred. Please try again later.";
      }

      const appError = new AppError({
        title: "Failed to Create Asset",
        response: error.response,
        userMessage,
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async getAssets({
    organisation_id,
    standard,
    page_size,
    page,
    search,
    ordering,
    pipe_type,
  }) {
    let mappedPipeType = pipe_type;
    if (pipe_type === "Sewer") {
      mappedPipeType = "SS";
    } else if (pipe_type === "Stormwater") {
      mappedPipeType = "SW";
    }

    let snakeCaseOrdering = ordering;
    if (ordering) {
      snakeCaseOrdering = ordering.replace(/([A-Z])/g, "_$1").toLowerCase();
      if (ordering.startsWith("-")) {
        const field = ordering.slice(1);
        const snakeField = field.replace(/([A-Z])/g, "_$1").toLowerCase();
        snakeCaseOrdering = `-${snakeField}`;
      }
    }

    try {
      const { data } = await apiClient.get("/assets", {
        params: {
          organisation_id,
          standard,
          page_size,
          page,
          search,
          ordering: snakeCaseOrdering,
          pipe_type: mappedPipeType,
        },
      });
      const assets = data.results.map((item) => Asset.fromServerModel(item));
      return { assets, count: data.count };
    } catch (error) {
      // A 404 means the requested page is out of range (e.g. a persisted page
      // that no longer exists for the current org). Surface the raw error so
      // the caller can snap back to a valid page instead of toasting.
      if (error.response?.status === 404) {
        throw error;
      }
      let userMessage = "Unable to load assets list. Please try again.";

      if (error.response?.data?.detail) {
        userMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        userMessage = error.response.data.error;
      } else if (error.response?.status === 403) {
        userMessage = "You don't have permission to view assets.";
      } else if (error.response?.status === 400) {
        userMessage = "Invalid search parameters. Please adjust your filters.";
      } else if (error.response?.status >= 500) {
        userMessage = "Server error occurred. Please try again later.";
      }

      const appError = new AppError({
        title: "Failed to Fetch Assets",
        response: error.response,
        userMessage,
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async getLinkedInspections(assetUuid, { page_size, page, search, ordering }) {
    let snakeCaseOrdering = ordering;
    if (ordering) {
      snakeCaseOrdering = ordering.replace(/([A-Z])/g, "_$1").toLowerCase();
      if (ordering.startsWith("-")) {
        const field = ordering.slice(1);
        const snakeField = field.replace(/([A-Z])/g, "_$1").toLowerCase();
        snakeCaseOrdering = `-${snakeField}`;
      }
    }

    try {
      const { data } = await apiClient.get(`/assets/${assetUuid}/inspections`, {
        params: { page_size, page, search, ordering: snakeCaseOrdering },
      });
      const inspections = data.results.map((item) =>
        Inspection.fromLinkedInspectionServerModel(item)
      );
      return { inspections, count: data.count };
    } catch (error) {
      let userMessage =
        "Unable to load inspections for this asset. Please try again.";

      if (error.response?.data?.detail) {
        userMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        userMessage = error.response.data.error;
      } else if (error.response?.status === 404) {
        userMessage = "Asset not found or has no linked inspections.";
      } else if (error.response?.status === 403) {
        userMessage =
          "You don't have permission to view this asset's inspections.";
      } else if (error.response?.status === 400) {
        userMessage = "Invalid search parameters. Please adjust your filters.";
      } else if (error.response?.status >= 500) {
        userMessage = "Server error occurred. Please try again later.";
      }

      const appError = new AppError({
        title: "Failed to Fetch Linked Inspections",
        response: error.response,
        userMessage,
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async deleteAsset(assetUuid) {
    try {
      await apiClient.delete(`/assets/${assetUuid}`);
      showSuccessNotification("Success", "Asset deleted successfully!");
    } catch (error) {
      let userMessage = "Unable to delete asset. Please try again.";

      if (error.response?.data?.detail) {
        userMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        userMessage = error.response.data.error;
      } else if (error.response?.status === 404) {
        userMessage = "Asset not found. It may have already been deleted.";
      } else if (error.response?.status === 403) {
        userMessage = "You don't have permission to delete this asset.";
      } else if (error.response?.status === 409) {
        userMessage =
          "Asset cannot be deleted because it has linked inspections.";
      } else if (error.response?.status === 400) {
        userMessage = "Asset deletion not allowed in current state.";
      } else if (error.response?.status >= 500) {
        userMessage = "Server error occurred. Please try again later.";
      }

      const appError = new AppError({
        title: "Failed to Delete Asset",
        response: error.response,
        userMessage,
      });
      showErrorNotification(appError);
      throw appError;
    }
  },
};

export default AssetService;
